import React from 'react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Info, Upload, CheckCircle2, Loader2 } from 'lucide-react';

// Mobile- and desktop-friendly tooltip via Popover (click/tap)
export const InfoHint = ({ text, label = 'More info', testId }) => (
  <Popover>
    <PopoverTrigger asChild>
      <button
        type="button"
        aria-label={label}
        data-testid={testId}
        className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-slate-100 hover:bg-emerald-100 text-slate-500 hover:text-emerald-700 transition-colors ml-1.5"
      >
        <Info className="w-3 h-3" />
      </button>
    </PopoverTrigger>
    <PopoverContent side="top" className="w-72 text-xs leading-relaxed text-slate-700">
      {text}
    </PopoverContent>
  </Popover>
);

export const PreviewBanner = () => (
  <div
    className="border border-amber-300 bg-amber-50 rounded-lg p-3 mb-4 text-xs text-amber-900 leading-relaxed flex items-start gap-2"
    data-testid="succ-preview-banner"
  >
    <span className="font-semibold uppercase tracking-wide text-amber-800 mr-1">PREVIEW MODE</span>
    <span>
      This is a preview link &mdash; safe to walk through. Nothing you upload here will be stored, no admin will see it,
      the Trusted Contact is not notified, and this link auto-expires in 1 hour.
    </span>
  </div>
);

// Feb 2026 quorum redesign: real member name + discreet "N of 2" progress.
// Never names the other Trusted Contacts.
export const QuorumContextBanner = ({ memberName, quorumRequired, quorumVerifiedCount }) => (
  <div
    className="border border-emerald-200 bg-emerald-50/50 rounded-lg p-4 mb-5"
    data-testid="succ-quorum-context"
  >
    <p className="text-sm text-slate-800 leading-relaxed">
      <strong>{memberName}</strong> trusted three people with this responsibility. You are one of them.
      <span className="text-slate-600"> {quorumRequired} confirmations &mdash; from any {quorumRequired} of the three people {memberName} listed &mdash; will release the record they prepared.</span>
    </p>
    <p className="text-xs text-emerald-800 mt-2 flex items-center gap-1.5" data-testid="succ-quorum-progress">
      <CheckCircle2 className="w-3.5 h-3.5" />
      <span>
        <strong>{quorumVerifiedCount || 0} of {quorumRequired} confirmations gathered so far.</strong> You will not be told who the others are, and they will not be told about you.
      </span>
    </p>
  </div>
);

export const DocUploadCard = ({ doc, done, file, uploading, onFileChange, onUpload }) => (
  <div className="border border-slate-200 rounded-lg p-4 mb-3 bg-slate-50" data-testid={`succ-doc-${doc.type}`}>
    <div className="flex items-center justify-between flex-wrap gap-2">
      <div className="flex items-center">
        <span className="text-sm font-semibold text-slate-800">{doc.label}</span>
        <InfoHint testId={`succ-tip-${doc.type}`} text={doc.help} />
      </div>
      {done ? (
        <span className="text-xs text-emerald-700 font-medium flex items-center gap-1">
          <CheckCircle2 className="w-3.5 h-3.5" /> Uploaded
        </span>
      ) : (
        <span className="text-xs text-slate-500">Required</span>
      )}
    </div>
    {!done && (
      <div className="mt-3 flex flex-col sm:flex-row gap-2 items-stretch sm:items-center">
        <input
          type="file"
          accept=".pdf,.jpg,.jpeg,.png"
          onChange={(e) => onFileChange(doc.type, e.target.files?.[0])}
          className="text-sm flex-1"
          data-testid={`succ-file-${doc.type}`}
        />
        <Button size="sm" onClick={() => onUpload(doc.type)}
          disabled={!file || uploading === doc.type}
          className="bg-emerald-700 hover:bg-emerald-800"
          data-testid={`succ-upload-${doc.type}`}>
          {uploading === doc.type ? <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> Uploading</> : <><Upload className="w-3.5 h-3.5 mr-1" /> Upload</>}
        </Button>
      </div>
    )}
  </div>
);
